'use client';

import {useRouter} from 'next/navigation';
import {Divider} from 'antd';

import {generateCartItemId, useCartItems} from '@/app/cartContext';
import {Button} from '@/ui/button/button';

import {CartProductCard} from './cart-product-card/cart-product-card';
import {FooterItem} from './footer-item/footer-item';

import styles from './cart.module.css';
import {useGetCartSummary} from './utils';
import {useIsMobile} from '@/utils/use-is-mobile';
import {OptionalContainer} from '@/ui/optional-container';

type CartProps = {
    withButton?: boolean;
    deliveryPrice?: number;
    freeDelivery?: number;
    minOrder?: number;
};

export const Cart = (props: CartProps) => {
    const isMobile = useIsMobile();
    return (
        <div className={`${styles.cart} ${isMobile ? styles.isMobile : ''}`} aria-label="Корзина">
            <CartContent {...props} />
        </div>
    );
};

export const CartContent = ({freeDelivery, deliveryPrice, minOrder, withButton = true}: CartProps) => {
    const router = useRouter();
    const isMobile = useIsMobile();

    const handleCheckout = () => {
        router.push('/order');
    };

    const {items} = useCartItems();
    const {header, products, count, productsSum} = useGetCartSummary(items);
    const calculatedDeliveryPrice = freeDelivery && freeDelivery < productsSum ? 0 : deliveryPrice;
    const totalPrice = (calculatedDeliveryPrice ?? 0) + productsSum;

    return (
        <>
            <header className={`${styles.header} ${isMobile ? styles.isMobile : ''}`}>{header}</header>
            <ul className={`${styles.main} ${isMobile ? styles.isMobile : ''}`} aria-label="Список товаров">
                {items.map((item) => (
                    <CartProductCard key={generateCartItemId(item)} item={item} />
                ))}
            </ul>
            <div className={`${styles.footer} ${isMobile ? styles.isMobile : ''}`}>
                <OptionalContainer show={!isMobile || deliveryPrice !== undefined} className={styles.footerSection}>
                    {!isMobile && <FooterItem left={`${count} ${products}`} right={`${productsSum} ₽`} size="s" />}
                    {deliveryPrice !== undefined && (
                        <FooterItem
                            left={<div>Доставка</div>}
                            right={`${freeDelivery && freeDelivery < productsSum ? 0 : deliveryPrice} ₽`}
                            size="s"
                        />
                    )}
                </OptionalContainer>
                {(minOrder !== undefined || freeDelivery) && <Divider className={styles.divider} />}
                <OptionalContainer
                    show={minOrder !== undefined || (!!freeDelivery && !!deliveryPrice)}
                    className={styles.footerSection}
                >
                    {minOrder !== undefined && (
                        <FooterItem left={'Минимальный заказ'} right={`${minOrder} ₽`} size="s" />
                    )}
                    {freeDelivery && !!deliveryPrice && (
                        <FooterItem left={'Бесплатная доставка'} right={`от ${freeDelivery} ₽`} size="s" />
                    )}
                </OptionalContainer>

                {!isMobile && <Divider className={styles.divider} />}

                <div className={styles.footerSection}>
                    <FooterItem left={'Сумма заказа'} right={`${totalPrice} ₽`} size="m" />
                    {withButton && (
                        <Button type="primary" style={{width: '100%'}} onClick={handleCheckout}>
                            К оформлению заказа
                        </Button>
                    )}
                </div>
            </div>
        </>
    );
};
